import { FC } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import { BasicDialog } from "../../BasicDialog";
import { useCurrentAppVersions } from "../../../hooks/useAppVersions";
import { APP_VERSION_PLATFORMS } from "../../../domain/appVersion.interface";

interface AppVersionsDialogProps {
	open: boolean;
	onClose: () => void;
}

/**
 * SIMS-9441 — read-only view of the current version of every component, in
 * the system's dialog style. Components with no registered version show a
 * dash.
 */
const AppVersionsDialog: FC<AppVersionsDialogProps> = ({ open, onClose }) => {
	const {
		data: current = [],
		isLoading,
		isError,
	} = useCurrentAppVersions(open);

	if (!open) {
		return null;
	}

	return (
		<BasicDialog
			title="Versions"
			onCancel={onClose}
			sx={{ "& .MuiDialog-paper": { maxWidth: 520 } }}
			actions={
				<Button size="large" variant="contained" onClick={onClose}>
					Close
				</Button>
			}
		>
			{isLoading ? (
				<Box display="flex" justifyContent="center" py={3}>
					<CircularProgress size={28} aria-label="Loading versions" />
				</Box>
			) : isError ? (
				<Typography color="error.main" sx={{ py: 2 }}>
					Versions couldn&apos;t be loaded.
				</Typography>
			) : (
				<Table size="small" aria-label="Current versions">
					<TableHead>
						<TableRow>
							<TableCell sx={{ fontWeight: 600 }}>Component</TableCell>
							<TableCell sx={{ fontWeight: 600 }}>Version</TableCell>
							<TableCell sx={{ fontWeight: 600 }}>Since</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{APP_VERSION_PLATFORMS.map(({ value, label }) => {
							const version = current.find((v) => v.platform === value);
							return (
								<TableRow
									key={value}
									data-testid={`app-version-row-${value}`}
									sx={{ verticalAlign: "top" }}
								>
									<TableCell>{label}</TableCell>
									<TableCell>
										{version ? (
											<Chip
												label={version.version}
												size="small"
												color="primary"
												variant="outlined"
											/>
										) : (
											"—"
										)}
										{version?.releaseNotes ? (
											<Typography
												sx={{
													fontSize: "0.75rem",
													color: "text.secondary",
													whiteSpace: "pre-line",
													mt: 0.75,
												}}
											>
												{version.releaseNotes}
											</Typography>
										) : null}
									</TableCell>
									<TableCell sx={{ whiteSpace: "nowrap" }}>
										{version?.updatedAt
											? dayjs(version.updatedAt).format("MMM D, YYYY")
											: "—"}
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			)}
		</BasicDialog>
	);
};

export default AppVersionsDialog;
